import type { Session } from '@halo/chat-sdk-react';
import { AccountType, useAppStore, type Msg } from '@halo/chat-sdk-react';
import { Avatar, useItemExpireContext } from '@halo/ui-chat-react';
import { useShallowEnhance } from '@halo/zustand-enhance';
import { useIsMyMsg, useUserInfo } from '@/scripts/hooks';
import { useActionsStore } from '@/store/actions';
import { AvatarMenu } from '@/pages/main/messages/ChatContent/AvatarMenu';

// Message
export interface MsgAvatarProps {
  message: Msg;
  session: Session;
}

export const MsgAvatar = memo(function MsgAvatar({ message, session }: MsgAvatarProps) {
  const { user } = useUserInfo(message.owner_id);
  const fetchProfileUser = useActionsStore((store) => store.fetchProfileUser);
  const { setProfileRobot } = useActionsStore(useShallowEnhance(['setProfileRobot']));
  const { alwaysShowTime } = useItemExpireContext();
  const isMyMessage = useIsMyMsg(message);

  const emptyTitle = useMemo(() => {
    return !alwaysShowTime && isMyMessage;
  }, [alwaysShowTime, isMyMessage]);

  const isRobot = useMemo(() => user?.account_type === AccountType.Robot, [user?.account_type]);

  const onClick = useCallback(() => {
    const sessionStoreInstance = useAppStore.getState().sessionStores[session.chat_id];
    const readonly = !sessionStoreInstance?.getState().groupAccess?.canPrivateMessage;
    if (isRobot && user?.user_id) {
      setProfileRobot(user.user_id);
      return;
    }
    if (user?.user_id) {
      fetchProfileUser(user.user_id, true, readonly);
    }
  }, [fetchProfileUser, session.chat_id, isRobot, user, setProfileRobot]);

  return (
    <AvatarMenu session={session} user={user} fetchProfileUser={onClick}>
      <Avatar cursor="pointer" mt={emptyTitle ? '18px' : '0'} src={user?.avatar} />
    </AvatarMenu>
  );
});
